// src/components/SponsorMapTab.js
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { openPrintWindowOptions } from '../utils/printUtils';
import { copyText } from '../utils/clipboardUtils';
import AdminCopyMenu from './admin/AdminCopyMenu';
import './SponsorMapTab.css';

const MAP_SVG_URL = '/Sponsor Alley blocks.svg';

// Slot rect in local coords (same for every Slot_* group in the SVG)
const SLOT_RECT = { x: 814.913, y: 269.523, width: 77.426, height: 67.056 };
const PAD = 2;

/** FM-style slot heading (e.g. Slot_3 → "SLOT 3"). */
function slotHeadingLabel(slotId) {
  const n = String(slotId || '').replace(/^Slot_/i, '');
  return n ? `SLOT ${n}` : String(slotId || '');
}

// Type order for sidebar: interactive first, then exhibit (vendor), then prize; alphabetically within each
const TYPE_ORDER = { interactive: 0, exhibit: 1, prize: 2, none: 3 };
function sortSponsorsForSlots(sponsors) {
  return [...sponsors].sort((a, b) => {
    const orderA = TYPE_ORDER[a.type] ?? 4;
    const orderB = TYPE_ORDER[b.type] ?? 4;
    if (orderA !== orderB) return orderA - orderB;
    return (a.name || '').localeCompare(b.name || '');
  });
}

const SponsorMapTab = ({
  sponsors = [],
  slotAssignments = {},
  onSlotAssignmentsChange,
  saving = false,
}) => {
  const containerRef = useRef(null);
  const [svgMarkup, setSvgMarkup] = useState('');
  const [svgError, setSvgError] = useState('');
  const [slotIds, setSlotIds] = useState([]);
  const [slotBackground, setSlotBackground] = useState(() => {
    if (typeof window === 'undefined') return {};
    try {
      const raw = window.localStorage.getItem('fm-sponsor-map-bg');
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }); // { Slot_0: true } => keep white background even with logos

  // Fetch the map SVG
  useEffect(() => {
    let cancelled = false;
    setSvgError('');
    fetch(MAP_SVG_URL)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load map');
        return res.text();
      })
      .then((text) => {
        if (!cancelled) setSvgMarkup(text);
      })
      .catch((err) => {
        if (!cancelled) setSvgError(err.message || 'Failed to load map SVG');
      });
    return () => { cancelled = true; };
  }, []);

  // Set SVG into container once when markup is available (so we don't wipe injected logos on re-render)
  useEffect(() => {
    if (!containerRef.current || !svgMarkup) return;
    containerRef.current.innerHTML = svgMarkup;
  }, [svgMarkup]);

  // Parse slot ids from SVG markup
  useEffect(() => {
    if (!svgMarkup) {
      setSlotIds([]);
      return;
    }
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgMarkup, 'image/svg+xml');
    const nodes = doc.querySelectorAll('[id^="Slot_"]');
    const ids = Array.from(nodes)
      .map((n) => n.id)
      .filter(Boolean)
      .sort((a, b) => {
        const numA = parseInt(a.replace('Slot_', ''), 10);
        const numB = parseInt(b.replace('Slot_', ''), 10);
        if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
        return String(a).localeCompare(b);
      });
    setSlotIds(ids);
  }, [svgMarkup]);

  // Inject slot numbers, hide white rect when filled, and inject logos
  const injectLogos = useCallback(() => {
    const container = containerRef.current;
    if (!container || !svgMarkup) return;
    const svg = container.querySelector('svg');
    if (!svg) return;

    const ns = 'http://www.w3.org/2000/svg';
    const slotW = SLOT_RECT.width - PAD * 2;
    const slotH = SLOT_RECT.height - PAD * 2;
    const baseX = SLOT_RECT.x + PAD;
    const baseY = SLOT_RECT.y + PAD;

    slotIds.forEach((slotId) => {
      const group = svg.querySelector(`[id="${slotId}"]`);
      if (!group) return;

      // Remove previously injected layers
      const oldLogos = group.querySelector('.sponsor-map-slot-logos');
      if (oldLogos) oldLogos.remove();
      const oldNumber = group.querySelector('.sponsor-map-slot-number');
      if (oldNumber) oldNumber.remove();

      const sponsorIds = slotAssignments[slotId] || [];
      const hasSponsors = sponsorIds.length > 0;
      const keepBg = !!slotBackground[slotId];

      // White rect: only show when user forces background on (empty or filled)
      const rect = group.querySelector('rect');
      if (rect) {
        rect.setAttribute('visibility', keepBg ? 'visible' : 'hidden');
      }

      // Slot number: show in center of slot (so we know which slot is which)
      const slotNum = slotId.replace('Slot_', '');
      const numberGroup = document.createElementNS(ns, 'g');
      numberGroup.setAttribute('class', 'sponsor-map-slot-number');
      const numberText = document.createElementNS(ns, 'text');
      numberText.setAttribute('x', String(SLOT_RECT.x + SLOT_RECT.width / 2));
      numberText.setAttribute('y', String(SLOT_RECT.y + SLOT_RECT.height / 2));
      numberText.setAttribute('text-anchor', 'middle');
      numberText.setAttribute('dominant-baseline', 'middle');
      numberText.setAttribute('font-size', hasSponsors ? '8' : '14');
      numberText.setAttribute('fill', hasSponsors ? 'rgba(0,0,0,0.35)' : '#555');
      numberText.textContent = slotNum;
      numberGroup.appendChild(numberText);
      group.appendChild(numberGroup);

      if (sponsorIds.length === 0) return;

      const logosGroup = document.createElementNS(ns, 'g');
      logosGroup.setAttribute('class', 'sponsor-map-slot-logos');

      const count = sponsorIds.length;

      sponsorIds.forEach((sid, idx) => {
        const sponsor = sponsors.find((s) => s.id === sid);
        if (!sponsor) return;
        const logoUrl = (sponsor.logo || '').trim();
        const useLogo = logoUrl && !logoUrl.includes('drive.google.com');

        if (useLogo) {
          const w = count === 1 ? slotW : slotW / count;
          const x = baseX + idx * w;
          const img = document.createElementNS(ns, 'image');
          img.setAttribute('href', logoUrl);
          img.setAttribute('x', String(x));
          img.setAttribute('y', String(baseY));
          img.setAttribute('width', String(w));
          img.setAttribute('height', String(slotH));
          img.setAttribute('preserveAspectRatio', 'xMidYMid meet');
          logosGroup.appendChild(img);
        } else {
          const w = count === 1 ? slotW : slotW / count;
          const x = baseX + idx * w;
          const text = document.createElementNS(ns, 'text');
          text.setAttribute('x', String(x + w / 2));
          text.setAttribute('y', String(baseY + slotH / 2));
          text.setAttribute('text-anchor', 'middle');
          text.setAttribute('dominant-baseline', 'middle');
          text.setAttribute('font-size', count === 1 ? '10' : '8');
          text.setAttribute('fill', '#333');
          text.textContent = (sponsor.name || 'Sponsor').slice(0, 12);
          logosGroup.appendChild(text);
        }
      });

      group.appendChild(logosGroup);
    });
  }, [svgMarkup, slotIds, slotAssignments, slotBackground, sponsors]);

  useEffect(() => {
    if (!svgMarkup || slotIds.length === 0) return;
    // Run after paint so the SVG is in the DOM
    const t = requestAnimationFrame(() => {
      injectLogos();
    });
    return () => cancelAnimationFrame(t);
  }, [svgMarkup, slotIds, injectLogos]);

  // Sponsors sorted: Interactive first, then Exhibit (vendor), then Prize; A–Z within each
  const sortedSponsors = useMemo(() => sortSponsorsForSlots(sponsors), [sponsors]);

  const handleSlotChange = (slotId, nextSponsorIds) => {
    const next = { ...slotAssignments };
    if (nextSponsorIds.length === 0) {
      delete next[slotId];
    } else {
      next[slotId] = nextSponsorIds;
    }
    onSlotAssignmentsChange(next);
  };

  const escapeHtml = (str) => {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  };

  const handlePrint = () => {
    const container = containerRef.current;
    if (!container) return;
    const svgEl = container.querySelector('svg');
    if (!svgEl) return;

    const legendEntries = slotIds
      .filter((id) => (slotAssignments[id] || []).length > 0)
      .map((id) => {
        const sponsorIds = slotAssignments[id] || [];
        const items = sponsorIds.map((sid) => {
          const s = sponsors.find((sp) => sp.id === sid);
          const name = s?.name || '—';
          const logoUrl = (s?.logo || '').trim();
          const useLogo = logoUrl && !logoUrl.includes('drive.google.com');
          return useLogo ? { name, logoUrl } : { name, logoUrl: null };
        });
        return { slot: id.replace('Slot_', 'Slot '), items };
      });

    const mapHtml = container.innerHTML;

    const legendBoxInner = legendEntries
      .map((e) =>
        e.items
          .map((it) =>
            it.logoUrl
              ? `<span class="legend-item"><img src="${it.logoUrl.replace(/"/g, '&quot;')}" alt="" />${escapeHtml(it.name)}</span>`
              : `<span class="legend-item">${escapeHtml(it.name)}</span>`
          )
          .join('')
      )
      .join('');

    const printBlock = `<div class="print-block"><div class="map-wrap">${mapHtml}</div><div class="legend"><h2>Legend</h2><div class="legend-box">${legendBoxInner}</div></div></div>`;
    const bodyHtml = `<div class="print-page">${printBlock}${printBlock}</div>`;

    const extraCss = `
      body { font-family: sans-serif; margin: 0.2in; }
      @page { margin: 0; }
      .print-page { display: flex; flex-direction: column; height: 10.6in; justify-content: space-between; }
      .print-block { width: 100%; box-sizing: border-box; page-break-inside: avoid; }
      .map-wrap { width: 100%; }
      .map-wrap svg { width: 100%; height: auto; max-height: 4.1in; }
      .legend { margin-top: 4px; break-inside: avoid; }
      .legend h2 { font-size: 11px; margin: 0 0 2px 0; }
      .legend-box { display: flex; flex-wrap: wrap; gap: 3px; font-size: 8px; }
      .legend-item { display: inline-flex; align-items: center; gap: 3px; padding: 1px 3px; border-radius: 3px; border: 1px solid #e2e8f0; }
      .legend-item img { width: 12px; height: 12px; object-fit: contain; }
      .sponsor-map-slot-number { display: none !important; }
    `;

    const ok = openPrintWindowOptions({
      title: 'Sponsor map',
      bodyHtml,
      extraCss,
      autoPrint: false,
    });
    if (!ok) {
      alert('Please allow pop-ups to print the map.');
    }
  };

  const copySlotAssignmentsTsv = () => {
    const header = ['Slot', 'Sponsors'].join('\t');
    const lines = slotIds.map((slotId) => {
      const ids = slotAssignments[slotId] || [];
      const names = ids
        .map((sid) => sponsors.find((s) => s.id === sid)?.name || sid)
        .join('; ');
      return [slotHeadingLabel(slotId), names].join('\t');
    });
    copyText([header, ...lines].join('\n'));
  };

  if (svgError) {
    return (
      <section className="sponsor-map-tab">
        <p className="sponsor-map-tab-error">{svgError}</p>
      </section>
    );
  }

  if (!svgMarkup) {
    return (
      <section className="sponsor-map-tab">
        <p className="sponsor-map-tab-loading">Loading map…</p>
      </section>
    );
  }

  return (
    <section className="sponsor-map-tab">
      <div className="sponsor-map-tab-actions">
        <button
          type="button"
          className="sponsor-map-tab-print-btn"
          onClick={handlePrint}
          disabled={saving}
        >
          Print map
        </button>
        <AdminCopyMenu
          ariaLabel="Copy map data"
          disabled={saving || slotIds.length === 0}
          className="sponsor-map-tab-copy-menu"
          items={[{ label: 'Copy slot assignments (TSV)', onSelect: () => copySlotAssignmentsTsv() }]}
        />
      </div>
      <div className="sponsor-map-tab-layout">
        <div
          className="sponsor-map-tab-map-wrap"
          ref={containerRef}
          aria-label="Dropzone map with sponsor slots"
        />
        <aside className="sponsor-map-tab-sidebar">
          <h3 className="sponsor-map-tab-sidebar-title">Slot assignments</h3>
          <p className="sponsor-map-tab-sidebar-hint">
            Assign one or more sponsors per slot (Ctrl/Cmd+click to multi-select). Multiple sponsors share the tent; icons shrink to fit.
          </p>
          <div className="sponsor-map-tab-slot-list">
            {slotIds.map((slotId) => {
              const selected = slotAssignments[slotId] || [];
              return (
                <div key={slotId} className="sponsor-map-tab-slot-row">
                  <div className="sponsor-map-tab-slot-header">
                    <span className="sponsor-map-tab-slot-label">{slotHeadingLabel(slotId)}</span>
                    <label className="sponsor-map-tab-slot-bg-toggle">
                      <input
                        type="checkbox"
                        className="sponsor-map-tab-slot-bg-checkbox"
                        checked={!!slotBackground[slotId]}
                        onChange={(e) => {
                          setSlotBackground((prev) => {
                            const next = { ...prev, [slotId]: e.target.checked };
                            try {
                              window.localStorage.setItem('fm-sponsor-map-bg', JSON.stringify(next));
                            } catch {
                              // ignore
                            }
                            return next;
                          });
                        }}
                      />
                      <span className="sponsor-map-tab-slot-bg-toggle-text">Keep white background</span>
                    </label>
                  </div>
                  <select
                    multiple
                    className="sponsor-map-tab-slot-select"
                    value={selected}
                    onChange={(e) => {
                      const opts = Array.from(e.target.selectedOptions, (o) => o.value);
                      handleSlotChange(slotId, opts);
                    }}
                    aria-label={`Sponsors for ${slotHeadingLabel(slotId)}`}
                  >
                    {sortedSponsors.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name || s.id}
                      </option>
                    ))}
                  </select>
                </div>
              );
            })}
          </div>
        </aside>
      </div>

      {/* Icon legend at bottom: slot → sponsor(s) with small logos */}
      <div className="sponsor-map-tab-legend">
        <div className="sponsor-map-tab-legend-grid">
          {slotIds
            .filter((id) => (slotAssignments[id] || []).length > 0)
            .map((slotId) => {
              const ids = slotAssignments[slotId] || [];
              const slotLabel = slotHeadingLabel(slotId);
              return (
                <div key={slotId} className="sponsor-map-tab-legend-row">
                  <span className="sponsor-map-tab-legend-slot">{slotLabel}</span>
                  <span className="sponsor-map-tab-legend-sponsors">
                    {ids.map((sid) => {
                      const s = sponsors.find((sp) => sp.id === sid);
                      if (!s) return null;
                      const logoUrl = (s.logo || '').trim();
                      const useLogo = logoUrl && !logoUrl.includes('drive.google.com');
                      return (
                        <span key={sid} className="sponsor-map-tab-legend-item">
                          {useLogo ? (
                            <img src={logoUrl} alt="" className="sponsor-map-tab-legend-icon" />
                          ) : null}
                          <span className="sponsor-map-tab-legend-name">{s.name || '—'}</span>
                        </span>
                      );
                    })}
                  </span>
                </div>
              );
            })}
        </div>
        {slotIds.every((id) => (slotAssignments[id] || []).length === 0) && (
          <p className="sponsor-map-tab-legend-empty">No slots assigned yet. Use the sidebar to assign sponsors.</p>
        )}
      </div>
    </section>
  );
};

export default SponsorMapTab;
